import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X } from "lucide-react";
import { isPlaceholder } from "../content/services";

function VideoItem({ item, testId }) {
  const ref = useRef(null);
  const play = () => ref.current?.play().catch(() => {});
  const pause = () => ref.current?.pause();

  return (
    <div
      data-testid={testId}
      className="group relative overflow-hidden rounded-xl border border-mist bg-navy"
      onMouseEnter={play}
      onMouseLeave={pause}
      onClick={() => (ref.current?.paused ? play() : pause())}
    >
      <video
        ref={ref}
        src={item.src}
        poster={item.poster}
        muted
        loop
        playsInline
        preload="none"
        className={`w-full object-cover ${item.ratio === "9x16" ? "aspect-[9/16]" : "aspect-[4/5]"}`}
      />
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-navy/20 opacity-100 transition-opacity duration-200 group-hover:opacity-0">
        <span className="rounded-full bg-white/90 p-3"><Play size={20} className="text-navy" /></span>
      </div>
      <p className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy/90 to-transparent p-3 text-xs font-medium text-white">
        {item.caption} {item.duration && <span className="text-white/60">({item.duration})</span>}
      </p>
    </div>
  );
}

function PlaceholderItem({ item, testId }) {
  return (
    <div
      data-testid={testId}
      className={`flex w-full flex-col justify-between rounded-xl border border-dashed border-mist bg-fog p-4 ${
        item.ratio === "9x16" ? "aspect-[9/16]" : item.ratio === "4x5" ? "aspect-[4/5]" : "aspect-square"
      }`}
    >
      <span className="text-xs font-bold uppercase tracking-widest text-navy/30">
        {item.type === "video" ? "Video" : "Image"} incoming
      </span>
      <p className="text-xs font-medium text-navy/50">{item.caption}</p>
    </div>
  );
}

const FORMATS = [
  { key: "static", label: "Statics" },
  { key: "video", label: "Videos" },
  { key: "ai", label: "AI videos" },
];

export function Gallery({ items, title = "The creative" }) {
  const [lightbox, setLightbox] = useState(null);
  const [format, setFormat] = useState("");
  const all = items.filter((i) => !isPlaceholder(i.caption));
  if (!all.length) return null;
  const formats = FORMATS.filter((f) => all.some((i) => i.format === f.key));
  const visible = format ? all.filter((i) => i.format === format) : all;

  return (
    <div data-testid="creative-gallery">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h2 className="text-[26px] font-bold leading-[1.15] text-navy md:text-[32px]">{title}</h2>
        {formats.length > 1 && (
          <div data-testid="gallery-format-filter" className="flex flex-wrap gap-2">
            {[{ key: "", label: `All (${all.length})` }, ...formats.map((f) => ({ ...f, label: `${f.label} (${all.filter((i) => i.format === f.key).length})` }))].map((f) => (
              <button
                key={f.key}
                type="button"
                data-testid={`gallery-format-${f.key || "all"}`}
                aria-pressed={format === f.key}
                onClick={() => setFormat(f.key)}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${format === f.key ? "bg-navy text-white" : "border border-mist bg-white text-navy hover:border-navy"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div key={format} className="mt-6 columns-2 gap-4 md:columns-3 [&>*]:mb-4">
        {visible.map((item, i) =>
          item.placeholder ? (
            <PlaceholderItem key={i} item={item} testId={`gallery-item-${i}`} />
          ) : item.type === "video" ? (
            <VideoItem key={i} item={item} testId={`gallery-item-${i}`} />
          ) : (
            <button
              key={i}
              data-testid={`gallery-item-${i}`}
              onClick={() => setLightbox(item)}
              className="block w-full overflow-hidden rounded-xl border border-mist text-left"
            >
              <img src={item.src} alt={item.caption} loading="lazy" className={`w-full object-cover ${item.ratio === "9x16" ? "aspect-[9/16]" : item.ratio === "4x5" ? "aspect-[4/5]" : "aspect-square"}`} />
              <span className="block bg-white p-3 text-xs font-medium text-navy/70">{item.caption}</span>
            </button>
          )
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-50 flex items-center justify-center bg-navy/90 p-5"
            onClick={() => setLightbox(null)}
          >
            <button data-testid="gallery-lightbox-close" aria-label="Close" className="absolute right-5 top-5 text-white">
              <X size={28} />
            </button>
            <motion.figure
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              className="max-h-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.src} alt={lightbox.caption} className="max-h-[80vh] w-auto rounded-xl" />
              <figcaption className="mt-3 text-center text-sm text-white/80">{lightbox.caption}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
